/**
 * View Component Registry
 *
 * Maps view component names (strings) to actual React components
 */

import React from 'react';

// Import production views
import DashboardPage from '@/views/dashboard/DashboardPage';
import VaultPage from '@/views/vault/VaultPage';
import DealPage from '@/views/deals/DealPage';
import DealPipelinePage from '@/views/deal-pipeline/DealPipelinePage';
import CalendarPage from '@/views/calendar/CalendarPage';
import InboxPage from '@/views/inbox/demo-ari/AriDemoInboxPage';
import DeliveryManagerPage from '@/views/delivery-manager/DeliveryManagerPage';
import PortfolioPage from '@/views/portfolio/PortfolioPage';
import IntegrationsPage from '@/views/integrations/IntegrationsPage';
import OrganizationsList from '@/views/organization/OrganizationsList';
import ChatPage from '@/views/chat/ChatPage';
import { DealCreationFlow } from '@/views/deals/components/DealCreationFlow';

const DealCreationView: React.FC = () => (
  <DealCreationFlow embedded onCancel={() => {}} />
);

const UploadDialogView: React.FC<{ mode?: 'primary' | 'auxiliary'; existingFiles?: string[] }> = ({
  mode = 'primary',
  existingFiles = [],
}) => {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-3xl font-bold text-white mb-2">
            {mode === 'primary' ? 'Upload Your Contract' : 'Add Related Documents'}
          </div>
          <div className="text-white/70">
            {mode === 'primary'
              ? 'Drag and drop your PDF or Word document'
              : 'Upload side letters, amendments, or supporting documents'}
          </div>
        </div>

        {/* Existing files */}
        {existingFiles.length > 0 && (
          <div className="mb-6">
            <div className="text-white/70 text-sm mb-2">Already uploaded:</div>
            <div className="space-y-2">
              {existingFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/10"
                >
                  <div className="text-2xl">📄</div>
                  <div className="flex-1 text-white text-sm">{file}</div>
                  <div className="text-green-400 text-sm">✓ Uploaded</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dropzone */}
        <div className="upload-dropzone relative">
          <div className="border-4 border-dashed border-primary/30 rounded-2xl p-12 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all cursor-pointer">
            <div className="text-center">
              <div className="text-6xl mb-4">📁</div>
              <div className="text-white text-lg font-medium mb-2">
                Drop your file here
              </div>
              <div className="text-white/50 text-sm mb-4">
                or click to browse
              </div>
              <div className="inline-block px-6 py-2 bg-primary rounded-full text-white text-sm font-medium">
                Select File
              </div>
              <div className="mt-4 text-white/40 text-xs">
                Supports PDF, DOC, DOCX up to 50MB
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contract Analysis View (already exists but we may need to wrap it)
// For now, we'll use a placeholder that shows the contract cards
const ContractAnalysisView: React.FC<{ contractId: string; mode?: 'eq' | 'iq' | 'signature' }> = ({
  contractId,
  mode = 'eq',
}) => {
  return (
    <div className="h-full overflow-auto p-8">
      <div className="max-w-6xl mx-auto">
        {mode === 'signature' ? (
          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-6">Ready to Sign</div>
            <div className="text-white/70 mb-8">Review one final time and sign below</div>

            {/* Signature Field */}
            <div className="max-w-2xl mx-auto">
              <div
                data-field="signature"
                className="border-4 border-dashed border-primary/30 rounded-xl p-12 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all cursor-pointer"
              >
                <div className="text-5xl mb-4">✍️</div>
                <div className="text-white text-lg font-medium mb-2">Click here to sign</div>
                <div className="text-white/50 text-sm">Legally binding e-signature</div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-2xl font-bold text-white mb-1">
                  Artist Recording Agreement
                </div>
                <div className="text-white/60 text-sm">
                  {mode === 'eq' ? 'Plain English View' : 'Detailed Legal Analysis'}
                </div>
              </div>
              <button
                data-tour="flip-card"
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm transition-colors"
              >
                Switch to {mode === 'eq' ? 'IQ' : 'EQ'} Mode
              </button>
            </div>

            {/* Contract Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Money Card */}
              <div
                data-card="moneyYouReceive"
                className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6 hover:scale-105 transition-transform cursor-pointer"
              >
                <div className="text-3xl mb-3">💰</div>
                <div className="text-white font-bold text-lg mb-2">Money You Receive</div>
                <div className="text-white/80 text-sm mb-4">
                  $150,000 advance + 50% royalty split after recoupment
                </div>
                <div className="text-green-400 text-xs">Good terms</div>
              </div>

              {/* Duration Card */}
              <div
                data-card="howLong"
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:scale-105 transition-transform cursor-pointer"
              >
                <div className="text-3xl mb-3">📅</div>
                <div className="text-white font-bold text-lg mb-2">How Long</div>
                <div className="text-white/80 text-sm mb-4">
                  5 album deal, rights revert after 7 years
                </div>
                <div className="text-white/60 text-xs">Standard duration</div>
              </div>

              {/* Risks Card */}
              <div
                data-card="risksCostsLegalStuff"
                className="bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/30 rounded-xl p-6 hover:scale-105 transition-transform cursor-pointer"
              >
                <div className="text-3xl mb-3">⚠️</div>
                <div className="text-white font-bold text-lg mb-2">Risks & Legal</div>
                <div className="text-white/80 text-sm mb-4">
                  Territory exclusivity, 100% recoupment clause
                </div>
                <div className="text-red-400 text-xs">Needs review</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Registry mapping
export const VIEW_REGISTRY: Record<string, React.ComponentType<any>> = {
  'dashboard': DashboardPage,
  'vault': VaultPage,
  'deal-page': DealPage,
  'deal-creation': DealCreationView,
  'upload-dialog': UploadDialogView,
  'contract-analysis': ContractAnalysisView,
  'deal-pipeline': DealPipelinePage,
  'calendar': CalendarPage,
  'inbox': InboxPage,
  'delivery-manager': DeliveryManagerPage,
  'portfolio': PortfolioPage,
  'integrations': IntegrationsPage,
  'organizations': OrganizationsList,
  'chat-interface': ChatPage,
};

/**
 * Get a view component by name
 */
export function getViewComponent(viewName: string): React.ComponentType<any> | null {
  return VIEW_REGISTRY[viewName] || null;
}

/**
 * Register a custom view component
 */
export function registerViewComponent(viewName: string, component: React.ComponentType<any>) {
  VIEW_REGISTRY[viewName] = component;
}
